import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@/contexts/ThemeContext'
import { Button } from '@/components/ui/button'
import { Moon, Sun, Globe, ChevronRight, Bell, LogOut, ShieldCheck, Trash2 } from 'lucide-react'
import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import {
  setIosEnabled,
  setAndroidEnabled,
  subscribeIosEnabled,
  subscribeAndroidEnabled,
  syncIosFcmTokenFromPreferences,
  syncAndroidFcmTokenFromPreferences,
  cleanupIosOnSignOut,
} from '@/lib/notifications'
import { IosNotificationSettingsDialog } from '@/components/shared/IosNotificationSettingsDialog'
import { useAuthState } from '@/hooks/useAuth'
import { useUserData } from '@/hooks/useUserData'
import { useAdminMode } from '@/hooks/useAdminMode'
import { signOutUser } from '@/lib/firebase'
import { AuthModal } from '@/components/auth/AuthModal'
import { DeleteAccountDialog } from '@/components/shared/DeleteAccountDialog'
import { DELETE_USER_ACCOUNT_ENDPOINT } from '@/config/endpoints'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { SUPPORTED_APP_LANGUAGES } from '@/lib/supportedLanguages'

const APP_STORE_URL = 'https://apps.apple.com/fi/app/getmoretats/id6754553245'
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.bareminimum.getmoretats'

interface AppSettingsProps {
  showLogout?: boolean
  onLogoutSuccess?: () => void
}

export function AppSettings({ showLogout = true, onLogoutSuccess }: AppSettingsProps) {
  const { t, i18n } = useTranslation()
  const { toggleThemeMode, isDarkMode } = useTheme()
  const navigate = useNavigate()
  const [isLanguageSheetOpen, setIsLanguageSheetOpen] = useState(false)
  const { user } = useAuthState()
  const { userData } = useUserData()
  const { adminModeEnabled, toggleAdminMode } = useAdminMode()
  const [pushEnabled, setPushEnabled] = useState<boolean>(false)
  const [updating, setUpdating] = useState<boolean>(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false)
  const [showNotificationDeniedDialog, setShowNotificationDeniedDialog] = useState(false)

  const isIOSNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios'
  const isIOSWeb = !Capacitor.isNativePlatform() && /iPad|iPhone|iPod/i.test(navigator.userAgent)
  const isAndroidNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android'
  const isAndroidWeb = !Capacitor.isNativePlatform() && /Android/i.test(navigator.userAgent)
  /** In-app push toggle: iOS app or Android app */
  const showNativePushToggle = isIOSNative || isAndroidNative
  /** Mobile browser: link to store (no in-browser push in this product). */
  const showMobileWebStoreCta = isIOSWeb || isAndroidWeb

  useEffect(() => {
    if (!user?.uid || !isIOSNative) return
    const unsub = subscribeIosEnabled(user.uid, setPushEnabled)
    return () => unsub()
  }, [user?.uid, isIOSNative])

  useEffect(() => {
    if (!user?.uid || !isAndroidNative) return
    const unsub = subscribeAndroidEnabled(user.uid, setPushEnabled)
    return () => unsub()
  }, [user?.uid, isAndroidNative])

  const handleToggleNotifications = async () => {
    if (!user?.uid) {
      setShowAuthModal(true)
      return
    }
    const next = !pushEnabled
    try {
      setUpdating(true)
      if (next) {
        if (isIOSNative || isAndroidNative) {
          try {
            const { receive } = await PushNotifications.checkPermissions()
            if (receive === 'denied') {
              setShowNotificationDeniedDialog(true)
              return
            }
          } catch {
            /* fall through */
          }
        }
        if (isIOSNative) {
          const syncSucceeded = await syncIosFcmTokenFromPreferences(user.uid)
          if (!syncSucceeded) setShowNotificationDeniedDialog(true)
        } else if (isAndroidNative) {
          const syncSucceeded = await syncAndroidFcmTokenFromPreferences(user.uid)
          if (!syncSucceeded) setShowNotificationDeniedDialog(true)
        }
      } else {
        if (isIOSNative) await setIosEnabled(user.uid, false)
        else if (isAndroidNative) await setAndroidEnabled(user.uid, false)
      }
    } finally {
      setUpdating(false)
    }
  }

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode)
    setIsLanguageSheetOpen(false)
  }

  const handleLogout = async () => {
    try {
      console.log('[AppSettings] Starting sign-out...')
      if (Capacitor.isNativePlatform() && (Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android')) {
        await cleanupIosOnSignOut()
      }
      await signOutUser()
      onLogoutSuccess?.()
    } catch (error: any) {
      console.error('[AppSettings] Error signing out:', error)
      alert(t('auth.signOutError') + ': ' + (error?.message || 'Unknown error'))
    }
  }

  const handleDeleteAccount = async () => {
    try {
      if (!user?.uid) return

      const idToken = await user.getIdToken()

      const response = await fetch(DELETE_USER_ACCOUNT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          userId: user.uid
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || 'Failed to delete account')
      }

      setShowDeleteAccountDialog(false)
      await signOutUser()
      navigate('/')
      onLogoutSuccess?.()
    } catch (error: any) {
      console.error('[AppSettings] Error deleting account:', error)
      alert(t('settings.deleteAccountError', 'Error deleting account') + ': ' + (error?.message || 'Unknown error'))
    }
  }

  const currentLanguage = SUPPORTED_APP_LANGUAGES.find(lang => lang.code === i18n.language) || SUPPORTED_APP_LANGUAGES[0]

  return (
    <>
      <div className="flex flex-col gap-1">
        {userData?.isAdmin && (
          <div className="w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-lg bg-card overflow-hidden transition-colors hover:bg-muted/50">
            <div className="flex items-center gap-3 min-w-0">
              <ShieldCheck className="h-5 w-5 shrink-0 text-foreground" />
              <div className="text-left min-w-0">
                <p className="text-sm font-medium text-foreground">{t('settings.adminMode', 'Admin Mode')}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {adminModeEnabled
                    ? t('settings.adminModeDesc', 'View as admin')
                    : t('settings.adminModeDescOff', 'View as regular user')}
                </p>
              </div>
            </div>
            <Switch
              checked={adminModeEnabled}
              onCheckedChange={(checked) => {
                if (checked !== adminModeEnabled) toggleAdminMode()
              }}
              aria-label={t('settings.adminMode', 'Admin Mode')}
              className="shrink-0"
            />
          </div>
        )}

        {(showNativePushToggle || showMobileWebStoreCta) && (
          <div
            className={cn(
              'w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-lg bg-card overflow-hidden text-left transition-colors hover:bg-muted/50',
              !user && showNativePushToggle && 'cursor-pointer'
            )}
            onClick={() => {
              if (!user && showNativePushToggle) setShowAuthModal(true)
            }}
            onKeyDown={(e) => {
              if (!user && showNativePushToggle && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault()
                setShowAuthModal(true)
              }
            }}
            role={!user && showNativePushToggle ? 'button' : undefined}
            tabIndex={!user && showNativePushToggle ? 0 : undefined}
          >
            <div className="flex items-center gap-3 min-w-0">
              <Bell className="h-5 w-5 shrink-0 text-foreground" />
              <div className="text-left min-w-0">
                <p className="text-sm font-medium text-foreground">{t('settings.notifications', 'Notifications')}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {!user
                    ? t('auth.login')
                    : showMobileWebStoreCta
                      ? t('settings.notificationsUseAppHint', 'Use the app for push alerts')
                      : pushEnabled
                        ? t('common.enabled', 'Enabled')
                        : t('common.disabled', 'Disabled')}
                </p>
              </div>
            </div>
            {showNativePushToggle && user ? (
              <Switch
                checked={pushEnabled}
                disabled={updating}
                onCheckedChange={(checked) => {
                  if (checked !== pushEnabled) void handleToggleNotifications()
                }}
                onClick={(e) => e.stopPropagation()}
                aria-label={t('settings.notifications', 'Notifications')}
                className="shrink-0"
              />
            ) : showMobileWebStoreCta ? (
              <a
                href={isIOSWeb ? APP_STORE_URL : PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="inline-flex items-center justify-center h-9 px-4 rounded-md bg-primary text-white text-xs font-medium">
                  {t('common.downloadApp', 'Download App')}
                </span>
              </a>
            ) : (
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  setShowAuthModal(true)
                }}
                className="text-xs font-medium shrink-0"
              >
                {t('auth.login')}
              </Button>
            )}
          </div>
        )}

        <button
          onClick={() => setIsLanguageSheetOpen(true)}
          className="w-full flex items-center justify-between px-4 py-3.5 rounded-lg bg-card overflow-hidden hover:bg-muted/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Globe className="h-5 w-5 text-foreground" />
            <div className="text-left">
              <p className="text-sm font-medium text-foreground">{t('menu.language')}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {currentLanguage.flag} {currentLanguage.name}
              </p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-foreground" />
        </button>

        <button
          onClick={toggleThemeMode}
          className="w-full flex items-center justify-between px-4 py-3.5 rounded-lg bg-card overflow-hidden hover:bg-muted/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            {isDarkMode ? (
              <Moon className="h-5 w-5 text-foreground" />
            ) : (
              <Sun className="h-5 w-5 text-foreground" />
            )}
            <div className="text-left">
              <p className="text-sm font-medium text-foreground">{t('settings.theme')}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {isDarkMode ? t('theme.night') : t('theme.light')}
              </p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-foreground" />
        </button>

        {showLogout && user && (
          <button
            onClick={() => setShowDeleteAccountDialog(true)}
            className="w-full flex items-center justify-between px-4 py-3.5 rounded-lg bg-card overflow-hidden hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Trash2 className="h-5 w-5 text-foreground" />
              <div className="text-left">
                <p className="text-sm font-medium text-foreground">{t('settings.deleteAccount', 'Delete Account')}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {t('settings.deleteAccountDesc', 'Delete your account and all its data')}
                </p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-foreground" />
          </button>
        )}

        {showLogout && user && (
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-primary hover:opacity-90 transition-opacity text-white"
          >
            <LogOut className="h-4 w-4" />
            <span className="text-sm font-medium">{t('common.logout', 'Log Out')}</span>
          </button>
        )}
      </div>

      <DeleteAccountDialog
        isOpen={showDeleteAccountDialog}
        onOpenChange={setShowDeleteAccountDialog}
        onConfirm={handleDeleteAccount}
      />

      <Sheet open={isLanguageSheetOpen} onOpenChange={setIsLanguageSheetOpen}>
        <SheetContent side="bottom" className="h-[40vh] max-h-[300px] flex flex-col p-6">
          <SheetHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <SheetTitle className="text-xl font-semibold">{t('language.title')}</SheetTitle>
          </SheetHeader>

          <div className="flex-1 flex flex-col justify-center">
            <div className="space-y-3">
              {SUPPORTED_APP_LANGUAGES.map((language) => (
                <Button
                  key={language.code}
                  variant={i18n.language === language.code ? 'default' : 'ghost'}
                  className="w-full justify-start h-16 text-base font-medium"
                  onClick={() => handleLanguageChange(language.code)}
                >
                  <div className="flex items-center gap-4 w-full">
                    <span className="text-2xl">{language.flag}</span>
                    <span>{language.name}</span>
                    {i18n.language === language.code && (
                      <span className="ml-auto text-sm opacity-70">✓</span>
                    )}
                  </div>
                </Button>
              ))}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AuthModal
        isOpen={showAuthModal}
        onOpenChange={setShowAuthModal}
        onSuccess={() => {
          setShowAuthModal(false)
        }}
        source="settings_notifications"
        defaultMode="signup"
      />

      <IosNotificationSettingsDialog
        isOpen={showNotificationDeniedDialog}
        onOpenChange={setShowNotificationDeniedDialog}
      />
    </>
  )
}
